<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <div class="rd-chips" role="group" :aria-label="$t('order_status')">
        <button
          v-for="chip in statusChips"
          :key="chip.value"
          type="button"
          class="rd-chip"
          :aria-pressed="String(orderStatus === chip.value)"
          @click="orderStatus = chip.value"
        >
          {{ chip.label }}
          <b class="rd-num">{{ chip.count }}</b>
        </button>
      </div>
    </report-tools>

    <report-state v-if="loading && !users.length" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-off-outline" :title="emptyText"></report-state>

    <div v-else class="rd-card rd-card--scroll">
      <table class="rd-tbl rd-tbl--workers">
        <thead>
          <tr>
            <th scope="col" class="rd-col-id" :aria-sort="ariaSort('id')">
              <button type="button" @click="sortBy('id')">ID {{ sortArrow("id") }}</button>
            </th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("worker") }} {{ sortArrow("name") }}</button>
            </th>
            <th v-if="hasExternalCode" scope="col" :aria-sort="ariaSort('externalCode')">
              <button type="button" @click="sortBy('externalCode')">
                {{ $t("external_code") }} {{ sortArrow("externalCode") }}
              </button>
            </th>
            <th v-if="showCompanyColumn" scope="col" :aria-sort="ariaSort('company')">
              <button type="button" @click="sortBy('company')">{{ $t("firm") }} {{ sortArrow("company") }}</button>
            </th>
            <th
              v-if="isSingleDay"
              scope="col"
              class="rd-col-time"
              :title="$t('order_datetime')"
              :aria-sort="ariaSort('sortTime')"
            >
              <button type="button" @click="sortBy('sortTime')">{{ $t("time") }} {{ sortArrow("sortTime") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('ordered')">
              <button type="button" @click="sortBy('ordered')">{{ $t("ordered") }} {{ sortArrow("ordered") }}</button>
            </th>
            <template v-if="canCheckTaken">
              <th scope="col" class="is-right rd-col-taken" :aria-sort="ariaSort('taken')">
                <button type="button" @click="sortBy('taken')">{{ $t("taken") }} {{ sortArrow("taken") }}</button>
              </th>
              <th scope="col" class="is-right" :aria-sort="ariaSort('difference')">
                <button type="button" @click="sortBy('difference')">
                  {{ $t("difference") }} {{ sortArrow("difference") }}
                </button>
              </th>
            </template>
            <th scope="col" :aria-sort="ariaSort('stateRank')">
              <button type="button" @click="sortBy('stateRank')">{{ $t("status") }} {{ sortArrow("stateRank") }}</button>
            </th>
            <th scope="col" class="rd-col-go"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.key"
            data-open
            tabindex="0"
            :class="{ 'is-selected': isOpen(row), 'is-idle': row.state === 'off' }"
            @click="openWorker(row, $event)"
            @keydown.enter.prevent="openWorker(row, $event)"
            @keydown.space.prevent="openWorker(row, $event)"
          >
            <td class="rd-col-id rd-num rd-muted">{{ row.id }}</td>
            <td class="is-name" :title="row.name">
              <span class="rd-person">
                <span class="rd-avatar rd-avatar--sm">{{ row.initials }}</span>
                <span class="rd-person__name">{{ row.name }}</span>
              </span>
            </td>
            <td v-if="hasExternalCode" class="rd-num rd-muted">{{ row.externalCode || "–" }}</td>
            <td v-if="showCompanyColumn" class="rd-muted">{{ row.company || "–" }}</td>
            <td v-if="isSingleDay" class="rd-col-time rd-num rd-muted">{{ row.time || "–" }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.ordered }">{{ row.ordered }}</td>
            <template v-if="canCheckTaken">
              <td class="is-right rd-num rd-col-taken" :class="{ 'is-zero': !row.taken }">{{ row.taken }}</td>
              <td class="is-right rd-num" :class="{ 'is-zero': row.difference === 0, 'is-alert': row.difference > 0 }">
                {{ row.difference }}
              </td>
            </template>
            <td>
              <span class="rd-status" :class="`rd-status--${row.state}`">{{ row.statusLabel }}</span>
            </td>
            <td class="rd-col-go"><v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon></td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td class="rd-col-id"></td>
            <td>{{ $t("total") }}</td>
            <td v-if="hasExternalCode"></td>
            <td v-if="showCompanyColumn"></td>
            <td v-if="isSingleDay" class="rd-col-time"></td>
            <td class="is-right rd-num">{{ sums.ordered }}</td>
            <template v-if="canCheckTaken">
              <td class="is-right rd-num rd-col-taken">{{ sums.taken }}</td>
              <td class="is-right rd-num">{{ sums.difference }}</td>
            </template>
            <td></td>
            <td class="rd-col-go"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyReportTabEmployee from "@/components/company/reports/tabs/CompanyReportTabEmployee.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import ReportDesktopWorkerMonth from "@/components/report/desktop/screens/ReportDesktopWorkerMonth.vue";
import hasCompanyGroupEmployeeRows from "../../hasCompanyGroupEmployeeRows";

/**
 * Pregled korisnika firme tipa "grupa" na računaru: spisak radnika izabranih firmi sa poručeno / preuzeto /
 * razlikom i statusom (preuzimanje samo kad ga firma prati). Filter statusa su čipovi sa brojevima; klik na red
 * otvara mesečni pregled radnika u panelu. Firme, period i pretragu čita iz zajedničke trake, a radnike dobija od
 * ljuske (kao stari tab). Nasleđuje filter statusa, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopGroupEmployees",
  extends: CompanyReportTabEmployee,
  mixins: [hasCompanyGroupEmployeeRows, hasTableSort],
  components: { ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    hasExternalCode() {
      return this.matchedRows.some((row) => row.externalCode);
    },
    rows() {
      return this.sortRows(this.matchedRows);
    },
    kpis() {
      const kpis = [{ key: "ordered", label: this.$t("ordered"), value: this.sums.ordered, tone: "inverse" }];
      if (this.canCheckTaken) {
        kpis.push(
          { key: "taken", label: this.$t("taken"), value: this.sums.taken },
          { key: "difference", label: this.$t("difference"), value: this.sums.difference }
        );
      }
      return kpis;
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `worker-${row.id}`);
    },
    /** Mesečni pregled radnika u panelu (isti podaci kao stari dijalog: "food-orders-by-month"). */
    openWorker(row, event) {
      const employee = this.toEmployeeContext(row);
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `worker-${row.id}`,
        root: true,
        wide: true,
        title: row.name,
        subtitle: [`ID ${row.id}`, employee.companyName].filter(Boolean).join(" · "),
        component: ReportDesktopWorkerMonth,
        props: { compact: true, worker: employee },
      });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("worker"),
        ...(this.hasExternalCode ? [this.$t("external_code")] : []),
        ...(this.showCompanyColumn ? [this.$t("firm")] : []),
        ...(this.isSingleDay ? [this.$t("time")] : []),
        this.$t("ordered"),
        ...(this.canCheckTaken ? [this.$t("taken"), this.$t("difference")] : []),
        this.$t("status"),
      ];
      const rows = this.rows.map((row) => [
        row.id,
        row.name,
        ...(this.hasExternalCode ? [row.externalCode] : []),
        ...(this.showCompanyColumn ? [row.company] : []),
        ...(this.isSingleDay ? [row.time] : []),
        row.ordered,
        ...(this.canCheckTaken ? [row.taken, row.difference] : []),
        row.statusLabel,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
